// src/components/complaint/Complaint.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './complaint.css';
import Navbar from '../navbar/Navbar.js';
import AdBanner from '../ad_banner/AdBanner';
import { showToast } from '../Notify.js';
import { ToastContainer } from 'react-toastify';
import { insertComplaint, insertQuestion1, syncDirectToRDS } from '../../db/indexedDb.js';
import VirtualSuccessUI from '../virtual_ui/VirtualSuccessUI.js';
import { TIMEOUT, COMPLAINT_SUBMIT_TIMEOUT } from '../../config/index.js';
import { AppContext } from '../../context/AppContext';
import { enhancedSyncDataToCloud, getUnsyncedCount } from '../../db/enhancedIndexedDBSync';
import { statusDetector } from '../../utils/statusDetector';

const Complaint = () => {
  const [selectedItem, setSelectedItem] = useState('');
  const [timeLeft, setTimeLeft] = useState(15);
  const [showVirtualSuccess, setShowVirtualSuccess] = useState(false);
  const timerRef = useRef(null);
  const resetTime = TIMEOUT / 1000;

  const navigate = useNavigate();
  
  // ✅ Get complaint options from global context
  const { 
    response, 
    deck, 
    adBanners, 
    adsEnabled, 
    complaintOptions, 
    complaintSlideEnabled 
  } = useContext(AppContext);

  console.log('Complaint slide options from context:', JSON.stringify(complaintOptions));

  // match background color with the rating slide 
  const filteredDeck = deck.length > 0 ? deck.filter((slide) => slide.type === 'rating') : [];
  const filteredSlide = filteredDeck.length > 0 ? filteredDeck[0] : null;
  const background_color = filteredSlide?.background_color ?? '#fafafa';

  const device_id = localStorage.getItem('device_id');

  // ✅ Default complaint options (fallback if none configured)
  const DEFAULT_COMPLAINT_OPTIONS = [
    'Unfriendly Staff',
    'Products Unavailable',
    'Long Wait Times',
    'Store Cleanliness'
  ];

  // ✅ Check if complaint slide is disabled - redirect if so
  useEffect(() => {
    if (!complaintSlideEnabled && complaintOptions.length === 0) {
      console.log('Complaint slide is disabled or no options configured, redirecting to question1');
      // Only redirect if explicitly disabled (not just empty during initial load)
      const isInitialLoad = !localStorage.getItem('complaint_options_last_updated');
      if (!isInitialLoad) {
        navigate('/question1');
      }
    }
  }, [complaintSlideEnabled, complaintOptions, navigate]);

  const handleItemSelection = async (itemId) => {
    setSelectedItem(itemId);
    
    // Automatically submit when item is selected
    await handleSubmitResponse(itemId);
  };

  const getCurrentDateTimeUTC = () => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    const dateTimeUTC = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    return dateTimeUTC;
  };

  // src/components/complaint/Complaint.js - Updated submitResponse

const submitResponse = async (selectedComplaintId) => {
  try {
    const datetime_ = getCurrentDateTimeUTC();
    const device_type = localStorage.getItem('device_type');

    if (device_type === 'virtual') {
      try {
        const syncResult = await syncDirectToRDS({
          question1: [],
          question2: [],
          question3: [],
          complaint: [{
            device_id: device_id,
            complaint_id: selectedComplaintId, // ✅ Send complaint id
            datetime_: datetime_,
            customer_feedback_id: response.customer_feedback_id,
            synced: 1
          }]
        });

        if (!syncResult.success) {
          console.error('Failed to sync complaint to RDS, falling back to IndexedDB');
          await insertComplaint(device_id, datetime_, response.customer_feedback_id, selectedComplaintId);
        }
      } catch (error) {
        console.error('Error in direct RDS sync for complaint:', error);
        await insertComplaint(device_id, datetime_, response.customer_feedback_id, selectedComplaintId);
      }
    } else {
      await insertComplaint(device_id, datetime_, response.customer_feedback_id, selectedComplaintId);
    }

  } catch (error) {
    console.error('Error submitting complaint:', error);
    statusDetector.activeIssues.add('indexeddb-error');
    statusDetector.updateStatus({
      error: true,
      errorMessage: 'Failed to submit complaint data',
      errorDetails: error.message
    });
    alert('Error: There was an error submitting your response');
  }
};

  const handleSubmitResponse = async (itemId) => {
    const device_type = localStorage.getItem('device_type');
    
    // Clear the countdown timer first
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Submit the data
    await submitResponse(itemId);
    
    if (device_type === 'virtual') {
      // For virtual devices: show success screen and stay there
      setShowVirtualSuccess(true);
    } else {
      // For non-virtual devices: show toast and navigate back
      showToast('Your feedback is important to us. Thanks for reaching out!', 'success');
      setTimeout(() => {
        navigate('/question1');
      }, COMPLAINT_SUBMIT_TIMEOUT);
    }
  };

  useEffect(() => {
    startTimer(timeLeft);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startTimer = (durationInSeconds) => {
    console.log('startTimer', durationInSeconds);
    setTimeLeft(durationInSeconds);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === 1) {
          clearInterval(timerRef.current);

          const device_type = localStorage.getItem('device_type');

          // Submit response before navigating (only for non-virtual devices)
          const handleTimeout = async () => {
            try {
              await insertQuestion1(device_id, response.question1.response_value, response.question1.datetime_, {}, response.customer_feedback_id);

              // Only navigate for non-virtual devices
              if (device_type !== 'virtual') {
                navigate('/question1');
              } else {
                // For virtual devices, show success screen
                setShowVirtualSuccess(true);
              }
            } catch (error) {
              console.error('Error inserting question1 on timeout:', error);
              // Navigate anyway for non-virtual
              if (device_type !== 'virtual') {
                navigate('/question1');
              }
            }
          };
          handleTimeout();
        }

        return prev - 1;
      });
    }, 1000);
  };

  // Extend time by 10 more seconds
  const extendTime = () => {
    console.log(`timeLeft`, timeLeft);
    startTimer(timeLeft + 10);
  };

  return (
    showVirtualSuccess ? <VirtualSuccessUI /> :
    <div className='complaint-page-container' style={{ backgroundColor: background_color }}>
      <Navbar />
      <div className="complaint-container">
        <div className="complaint-title">Why was your experience not great today?</div>
        <div>
          <div className="complaint-scrollView">
            {complaintOptions.map(item => (
              <div
                key={item.complaint_id}
                className={`complaint-itemContainer ${selectedItem === item.complaint_id ? 'selectedItemContainer' : ''}`}
                onClick={() => handleItemSelection(item.complaint_id)}
              >
                <div className={`complaint-checkbox ${selectedItem === item.complaint_id ? 'checkedCheckbox' : ''}`}>
                  {selectedItem === item.complaint_id && <span className="checkmark">✔</span>}
                </div>
                <span className="complaint-itemText">{item.complaint_text}</span>
              </div>
            ))}
          </div>
        </div>

        <>
          <p className='complaint-countdown'>
            Returning to home in <strong>{timeLeft}</strong> seconds...
          </p>
          <button
            onClick={extendTime}
            className='complaint-more-time-btn'
          >
            Need more time?
          </button>
        </>
      </div>
      <ToastContainer />
      
      {/* ✅ Use dynamic ad banners from context */}
      {adsEnabled && <AdBanner images={adBanners} />}
    </div>
  );
};

export default Complaint;